/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from "react";
import { Image } from "primereact/image";
import { urlBaseAPI } from "../../utils";
import VisorArchivo, { EXTENSIONES_IMAGEN, obtenerExtension } from "./VisorArchivo";

export default function ArchivoPreview({
  nombre,
  hash,
  etiqueta,
  subtitulo,
  sinDescarga,
  sinToolbar
}) {
  const [visible, setVisible] = useState(false);
  // La imagen se monta recién al primer click para no descargar todas las
  // imágenes de una lista de antemano
  const [imagenMontada, setImagenMontada] = useState(false);
  const imagenRef = useRef(null);

  const extension = obtenerExtension(nombre);
  const esImagen = EXTENSIONES_IMAGEN.includes(extension);

  // Cuando se monta la imagen (primer click), abre el preview
  useEffect(() => {
    if (imagenMontada) {
      imagenRef.current?.show();
    }
  }, [imagenMontada]);

  // El texto del link puede ser otro que el nombre del archivo: en una lista de
  // documentos interesa el título del documento, no "REG-01_v3.pdf". La
  // extensión se sigue leyendo de `nombre`.
  const textoDelLink = etiqueta || nombre || "Archivo";

  // Con `subtitulo`, el link queda como texto principal y el subtítulo debajo
  // en gris (ej: la categoría del archivo como link y el nombre debajo)
  const conSubtitulo = (contenido) =>
    subtitulo ? (
      <div>
        <div>{contenido}</div>
        <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
          {subtitulo}
        </div>
      </div>
    ) : (
      contenido
    );

  if (!hash) {
    return conSubtitulo(<span>{textoDelLink}</span>);
  }

  const previewUrl = `${urlBaseAPI()}/archivo/${hash}/preview${
    !esImagen && sinToolbar ? "#toolbar=0" : ""
  }`;

  // La pantalla de vista previa sólo recibe el hash: se le avisa por la URL que
  // es una imagen para que la muestre con el visor de imágenes
  const urlPagina = `/archivo/${hash}${esImagen ? "?imagen=1" : ""}`;

  function handleClick(e) {
    e.preventDefault();
    // Cmd+click (Mac) o Ctrl+click (Windows/Linux) abre en nueva pestaña.
    //
    // Con sinDescarga NO se ofrece: la pantalla de vista previa es una URL, y
    // cualquier restricción que se le pase por ahí se saca editando la barra de
    // direcciones. El archivo restringido se ve solo en el visor embebido.
    if (!sinDescarga && (e.metaKey || e.ctrlKey)) {
      window.open(urlPagina, "_blank");
      return;
    }
    if (esImagen) {
      if (imagenMontada) {
        imagenRef.current?.show();
      } else {
        setImagenMontada(true);
      }
      return;
    }
    setVisible(true);
  }

  return (
    <>
      {conSubtitulo(
        <a
          href={sinDescarga ? undefined : urlPagina}
          onClick={handleClick}
          css={{
            color: "var(--principal)",
            cursor: "pointer",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {textoDelLink}
        </a>
      )}
      {esImagen ? (
        imagenMontada && (
          <Image
            ref={imagenRef}
            src={previewUrl}
            alt={nombre}
            preview
            css={{ display: "none" }}
          />
        )
      ) : (
        <VisorArchivo
          visible={visible}
          onHide={() => setVisible(false)}
          nombre={nombre}
          hash={hash}
          titulo={textoDelLink}
          sinToolbar={sinToolbar}
        />
      )}
    </>
  );
}
